/*!
 * SPDX-FileCopyrightText: 2023 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
import { showError, showInfo, showSuccess, showWarning } from '@nextcloud/dialogs';
import { t } from '@nextcloud/l10n';
import { join } from '@nextcloud/paths';
import { getUploader, hasConflict } from '@nextcloud/upload';
import { handleCopyMoveNodesTo, HintException } from '../actions/moveOrCopyAction.ts';
import { MoveCopyAction } from '../actions/moveOrCopyActionUtils.ts';
import { logger } from '../utils/logger.ts';
import { createDirectoryIfNotExists, Directory, renameInvalidDroppedEntries, resolveConflict, traverseTree } from './DropServiceUtils.ts';
/**
 * This function converts a list of DataTransferItems to a file tree.
 * It uses the Filesystem API if available, otherwise it falls back to the File API.
 * The File API will NOT be available if the browser is not in a secure context (e.g. HTTP).
 * ⚠️ When using this method, you need to use it as fast as possible, as the DataTransferItems
 * will be cleared after the first access to the props of one of the entries.
 *
 * @param items the list of DataTransferItems
 */
export async function dataTransferToFileTree(items) {
    // Check if the browser supports the Filesystem API
    // We need to cache the entries to prevent Blink engine bug that clears
    // the list (`data.items`) after first access props of one of the entries
    const entries = items
        .filter((item) => {
        if (item.kind !== 'file') {
            logger.debug('Skipping dropped item', { kind: item.kind, type: item.type });
            return false;
        }
        return true;
    }).map((item) => {
        // MDN recommends to try both, as it might be renamed in the future
        return item?.getAsEntry?.()
            ?? item?.webkitGetAsEntry?.()
            ?? item;
    });
    let warned = false;
    const fileTree = new Directory('root');
    // Traverse the file tree
    for (const entry of entries) {
        // Handle browser issues if Filesystem API is not available. Fallback to File API
        if (entry instanceof DataTransferItem) {
            logger.warn('Could not get FilesystemEntry of item, falling back to file');
            const file = entry.getAsFile();
            if (file === null) {
                logger.warn('Could not process DataTransferItem', { type: entry.type, kind: entry.kind });
                showError(t('files', 'One of the dropped files could not be processed'));
                continue;
            }
            // Warn the user that the browser does not support the Filesystem API
            // we therefore cannot upload directories recursively.
            if (file.type === 'httpd/unix-directory' || !file.type) {
                if (!warned) {
                    logger.warn('Browser does not support Filesystem API. Directories will not be uploaded');
                    showWarning(t('files', 'Your browser does not support the Filesystem API. Directories will not be uploaded'));
                    warned = true;
                }
                continue;
            }
            fileTree.contents.push(file);
            continue;
        }
        // Use Filesystem API
        try {
            fileTree.contents.push(await traverseTree(entry));
        }
        catch (error) {
            // Do not throw, as we want to continue with the other files
            logger.error('Error while traversing file tree', { error });
        }
    }
    return fileTree;
}
/**
 * Handle dropping external files
 *
 * @param root - The root directory which should be uploaded
 * @param destination - The destination folder
 * @param contents - The contents of the destination folder
 */
export async function onDropExternalFiles(root, destination, contents) {
    // Ask the user to fix any invalid name before uploading anything
    if (!await renameInvalidDroppedEntries(root)) {
        showInfo(t('files', 'Upload cancelled, drop the files again to retry'));
        return [];
    }
    const uploader = getUploader();
    // Check for conflicts on root elements
    if (await hasConflict(root.contents, contents)) {
        root.contents = await resolveConflict(root.contents, destination, contents);
        if (root.contents.length === 0) {
            // user cancelled the upload
            return [];
        }
    }
    else if (root.contents.length === 0) {
        logger.info('No files to upload', { root });
        showInfo(t('files', 'No files to upload'));
        return [];
    }
    // Let's process the files
    logger.debug(`Uploading files to ${destination.path}`, { root, contents: root.contents });
    const queue = [];
    let hasDirectoryErrors = false;
    const uploadDirectoryContents = async (directory, path) => {
        for (const file of directory.contents) {
            // This is the relative path to the resource
            // from the current uploader destination
            const relativePath = join(path, file.name);
            // If the file is a directory, we need to create it first
            // then browse its tree and upload its contents.
            if (file instanceof Directory) {
                try {
                    logger.debug('Processing directory', { relativePath, destination });
                    await createDirectoryIfNotExists(relativePath, destination);
                    await uploadDirectoryContents(file, relativePath);
                }
                catch (error) {
                    hasDirectoryErrors = true;
                    showError(t('files', 'Unable to create the directory {directory}', { directory: file.name }));
                    logger.error('Unable to create the directory', { error, relativePath, directory: file });
                }
                continue;
            }
            // If we've reached a file, we can upload it
            logger.debug('Uploading file to ' + join(destination.path, relativePath), { file });
            // Overriding the root to avoid changing the current uploader context
            queue.push(uploader.upload(relativePath, file, destination.source));
        }
    };
    // Pause the uploader to prevent it from starting
    // while we compute the queue
    uploader.pause();
    // Upload the files. Using '/' as the starting point
    // as we already adjusted the uploader destination
    await uploadDirectoryContents(root, '/');
    uploader.start();
    // Wait for all promises to settle
    const results = await Promise.allSettled(queue);
    // Check for errors
    const errors = results.filter((result) => result.status === 'rejected');
    if (errors.length > 0) {
        logger.error('Error while uploading files', { errors });
        showError(t('files', 'Some files could not be uploaded'));
    }
    if (errors.length > 0 || hasDirectoryErrors) {
        return [];
    }
    logger.debug('Files uploaded successfully');
    showSuccess(t('files', 'Files uploaded successfully'));
    return Promise.all(queue);
}
/**
 * Handle dropping internal files
 *
 * @param nodes - The nodes being dropped
 * @param destination - The destination folder
 * @param contents - The contents of the destination folder
 * @param isCopy - Whether the operation is a copy
 */
export async function onDropInternalFiles(nodes, destination, contents, isCopy = false) {
    // Check for conflicts on root elements
    if (await hasConflict(nodes, contents)) {
        nodes = await resolveConflict(nodes, destination, contents);
    }
    if (nodes.length === 0) {
        logger.info('No files to process', { nodes });
        showInfo(t('files', 'No files to process'));
        return;
    }
    try {
        const promises = Array.fromAsync(handleCopyMoveNodesTo(nodes, destination, isCopy ? MoveCopyAction.COPY : MoveCopyAction.MOVE));
        await promises;
        logger.debug('Files copy/move successful');
        showSuccess(isCopy ? t('files', 'Files copied successfully') : t('files', 'Files moved successfully'));
    }
    catch (error) {
        logger.error('Error while processing dropped files', { error });
        if (error instanceof HintException) {
            showError(error.message);
        }
        else {
            showError(isCopy ? t('files', 'Some files could not be copied') : t('files', 'Some files could not be moved'));
        }
    }
}
